// Local persistence for the org-roam index - the "saving it locally" half of
// org-roam-db-sync.
//
// org-roam keeps a SQLite file next to the notes. A browser has no such place,
// and organice's sync backends are for the user's own Org files rather than for
// a cache organice can rebuild at will - writing a database file into somebody's
// notes directory would be rude, and would sync a derived artifact across every
// device that shares the folder. So the index lives in the browser, per device,
// and is rebuilt by pressing sync.
//
// localStorage rather than IndexedDB, because organice already keeps every other
// piece of local state there and one storage mechanism is worth more than the
// extra headroom. The trade-off is a real quota - typically ~5 MB per origin -
// which is why lib/org_roam.js stores a deliberately narrow subset of
// org-roam's schema, and why a write that exceeds the quota is caught and
// reported rather than left to throw somewhere unrelated.

import { emptyDatabase, normalizeDatabase } from './org_roam';

export const STORAGE_KEY = 'orgRoamDatabase';

// Bumped when the stored shape changes in a way older data cannot satisfy. A
// mismatch drops the cache and asks for a re-sync, which costs one scan and is
// always safe: nothing here is a source of truth, only an index of the files.
// 2: links gained a `context` field (the sentence the link sits in) so the
// backlink list can show it. An index written by version 1 has no contexts and
// cannot grow them without re-reading the files, so it is dropped.
export const STORAGE_VERSION = 2;

const storage = () => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch (error) {
    // Access itself throws in some privacy modes.
    return null;
  }
};

/**
 * The stored index, or an empty one.
 *
 * Anything unreadable - absent, corrupt, or written by an older version - comes
 * back empty rather than throwing, so a bad cache costs a re-sync and never a
 * broken page.
 */
export const loadDatabase = () => {
  const store = storage();
  if (!store) return emptyDatabase();

  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return emptyDatabase();

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== STORAGE_VERSION) return emptyDatabase();

    return normalizeDatabase(parsed.database);
  } catch (error) {
    return emptyDatabase();
  }
};

/**
 * Persist the index. Returns `{ saved, error }` rather than throwing: the caller
 * is a page in the middle of a sync, and a full quota should be reported in the
 * UI, not lost in a promise.
 */
export const saveDatabase = (db) => {
  const store = storage();
  if (!store) return { saved: false, error: 'No local storage available in this browser.' };

  try {
    store.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: STORAGE_VERSION, database: normalizeDatabase(db) })
    );
    return { saved: true, error: null };
  } catch (error) {
    const isQuota =
      error &&
      (error.name === 'QuotaExceededError' ||
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
        error.code === 22);

    return {
      saved: false,
      error: isQuota
        ? 'The index is too large for this browser’s local storage. Narrow the roam directory and sync again.'
        : `Could not save the index: ${error.message || error}`,
    };
  }
};

export const clearDatabase = () => {
  const store = storage();
  if (store) {
    try {
      store.removeItem(STORAGE_KEY);
    } catch (error) {
      // Nothing useful to do: the cache is derived, and the next sync overwrites.
    }
  }

  return emptyDatabase();
};

/** Roughly how much of the quota the index is using, for the page to show. */
export const databaseSizeBytes = (db) => {
  try {
    return JSON.stringify({ version: STORAGE_VERSION, database: normalizeDatabase(db) }).length;
  } catch (error) {
    return 0;
  }
};
